import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  CheckCircle,
  XCircle,
  ToggleLeft,
  ToggleRight,
  ListChecks,
  LucideIcon,
  AlertCircle,
  Clock,
  Settings,
  UserCog,
  ArrowRight,
  BarChart3 as BarChartIcon
} from "lucide-react";
import { Link } from "react-router-dom"; // For navigation buttons
import { useAuth } from "@/contexts/AuthContext";
import { apiCall } from "@/utils/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface User {
  id: number;
  name: string;
  // Add other user properties if available from getSellers()
}

interface RecentLog {
  lead_id: number;
  user_name: string;
  timestamp: string;
}

interface DashboardData {
  isKommoConnected: boolean;
  assignationEnabled: boolean;
  sellers: User[]; // Full list of users
  activeSellers: User[]; // List of active users
  sellersCount: number;
  activeSellersCount: number;
  recentLogs: RecentLog[];
  totalLogsCount: number;
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  linkTo: string;
  buttonText: string;
  colorClass?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  description?: string;
  actionButton?: React.ReactNode;
  colorClass?: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  description,
  actionButton,
  colorClass,
  isLoading,
}) => (
  <Card className="border-slate-200 hover:shadow transition-shadow duration-200 overflow-hidden group">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
      <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{title}</CardTitle>
      <div className={cn("p-1.5 rounded-md bg-slate-50 group-hover:bg-white transition-colors duration-200 border border-slate-100", colorClass)}>
        <Icon className="h-3.5 w-3.5" />
      </div>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <div className="h-8 w-2/3 bg-slate-100 animate-pulse rounded-md mb-2"></div>
      ) : (
        <div className="text-2xl font-bold tracking-tight text-slate-900">{value}</div>
      )}
      {description && !isLoading && (
        <p className="text-[10px] font-medium text-slate-400 mt-1.5 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />
          {description}
        </p>
      )}
      {actionButton && <div className="mt-3 pt-3 border-t border-slate-50">{actionButton}</div>}
    </CardContent>
  </Card>
);

const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  icon: Icon,
  linkTo,
  buttonText,
  colorClass,
}) => (
  <Card className="h-full flex flex-col border-slate-200 hover:shadow transition-all duration-200 group rounded-lg overflow-hidden">
    <CardHeader className="pb-3">
      <div className="flex items-center space-x-3">
        <div className={cn(
          "p-2 rounded-lg transition-transform duration-200 group-hover:scale-105",
          colorClass ? "bg-white border border-slate-100" : "bg-primary/5 border border-primary/10"
        )}>
          <Icon className={cn("h-5 w-5", colorClass || "text-primary")} />
        </div>
        <CardTitle className="text-base font-bold text-slate-900">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="flex-grow">
      <p className="text-sm text-slate-500 font-medium leading-relaxed">{description}</p>
    </CardContent>
    <CardContent className="pt-0 pb-4 px-6">
      <Button asChild variant="secondary" className="w-full font-medium group-hover:bg-primary group-hover:text-white transition-all rounded-lg py-4 text-sm">
        <Link to={linkTo} className="flex items-center justify-center gap-2">
          {buttonText}
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Button>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const { token } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiCall<{
        success: boolean;
        dashboard: DashboardData;
        message?: string;
      }>(token, "/kommo/dashboard");

      console.log(result.dashboard);

      if (result.success && result.dashboard) {
        setDashboardData(result.dashboard);

      } else {
        throw new Error(result.message || "Failed to fetch dashboard data");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error fetching data";
      setError(errorMessage);
      toast.error(`Erreur de chargement: ${errorMessage}`);
      console.error("Fetch error:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleAssignation = async (): Promise<void> => {
    if (!dashboardData) return;

    // Optimistically update UI
    const originalAssignationStatus = dashboardData.assignationEnabled;
    setDashboardData((prevData) =>
      prevData
        ? { ...prevData, assignationEnabled: !prevData.assignationEnabled }
        : null
    );

    try {
      const result: ApiResponse<{ status: boolean }> =
        await apiCall(token, "/settings", "POST", {
          action: "toggle_assignation",
        });

      console.log(result);

      if (
        result.success &&
        typeof result.data?.status === "boolean"
      ) {
        // Confirm update from server
        setDashboardData((prevData) =>
          prevData
            ? {
              ...prevData,
              assignationEnabled: result.data!.status,
            }
            : null
        );

        // Show success toast
        const status = result.data.status ? "activée" : "désactivée";
        toast.success(`Assignation automatique ${status}`, {
          description: result.message || `L'assignation automatique a été ${status} avec succès.`
        });
      } else {
        // Revert optimistic update if server fails
        setDashboardData((prevData) =>
          prevData
            ? { ...prevData, assignationEnabled: originalAssignationStatus }
            : null
        );
        throw new Error(
          result.message || "Failed to toggle assignation status from server"
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Unknown error toggling assignation";
      setError(errorMessage);
      // Revert optimistic update on any error
      setDashboardData((prevData) =>
        prevData
          ? { ...prevData, assignationEnabled: originalAssignationStatus }
          : null
      );

      toast.error("Erreur lors du changement d'assignation", {
        description: errorMessage
      });
      console.error("Error toggling assignation:", errorMessage);
    }
  };

  if (loading && !dashboardData)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg text-muted-foreground">
            Chargement du tableau de bord...
          </p>
        </div>
      </div>
    );

  if (error && !dashboardData)
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-500 bg-red-50">
          <CardHeader>
            <div className="flex items-center text-red-600">
              <AlertCircle className="h-6 w-6 mr-2" />
              <CardTitle className="text-red-700">
                Erreur de chargement
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">{error}</p>
            <Button onClick={fetchData} variant="destructive" className="mt-4">
              Réessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );

  if (!dashboardData)
    return (
      <div className="mx-auto p-6 text-center">
        <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
        <p className="text-xl text-muted-foreground mb-4">
          Aucune donnée de tableau de bord n'a pu être chargée.
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Veuillez vérifier votre connexion ou réessayer plus tard.
        </p>
        <Button onClick={fetchData} variant="outline" size="lg">
          Réessayer
        </Button>
      </div>
    );

  const {
    isKommoConnected,
    assignationEnabled,
    sellersCount: usersCount,
    activeSellersCount: activeUsersCount,
    recentLogs,
    totalLogsCount,
    // users, // Full user list if needed for display
    // activeUsers // Full active user list if needed
  } = dashboardData;

  const stats: StatCardProps[] = [
    {
      title: "Statut Kommo",
      value: isKommoConnected ? "Connecté" : "Non connecté",
      icon: isKommoConnected ? CheckCircle : XCircle,
      description: isKommoConnected
        ? "Connexion API Kommo active."
        : "Vérifiez la configuration.",
      colorClass: isKommoConnected ? "text-green-600" : "text-red-600",
      actionButton: !isKommoConnected ? (
        <Button asChild size="sm" variant="outline">
          <Link to="/settings/kommo">Configurer</Link>
        </Button>
      ) : undefined,
      isLoading: loading,
    },
    {
      title: "Commerciaux Actifs",
      value: `${activeUsersCount} / ${usersCount}`,
      icon: Users,
      description: "Nombre de commerciaux actifs sur le total.",
      colorClass: "text-blue-600",
      isLoading: loading,
    },
    {
      title: "Assignation Auto.",
      value: assignationEnabled ? "Activée" : "Désactivée",
      icon: assignationEnabled ? ToggleRight : ToggleLeft,
      description: "Distribution automatique des leads.",
      actionButton: (
        <Button
          onClick={handleToggleAssignation}
          size="sm"
          variant={assignationEnabled ? "outline" : "default"}
          className={
            assignationEnabled
              ? "border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              : ""
          }
        >
          <div className="flex items-center">
            {assignationEnabled ? (
              <ToggleRight className="mr-2 h-4 w-4" />
            ) : (
              <ToggleLeft className="mr-2 h-4 w-4" />
            )}
            {assignationEnabled ? "Désactiver" : "Activer"}
          </div>
        </Button>
      ),
      colorClass: assignationEnabled ? "text-green-600" : "text-orange-600",
      isLoading: loading,
    },
    {
      title: "Assignations (Total)",
      value: totalLogsCount,
      icon: ListChecks,
      description: "Nombre total d'assignations effectuées.",
      colorClass: "text-purple-600",
      isLoading: loading,
    },
  ];

  const featureCards: FeatureCardProps[] = [
    {
      title: "Gestion des Commerciaux",
      description:
        "Gérez les utilisateurs Kommo, activez ou désactivez leurs comptes pour l'assignation.",
      icon: UserCog,
      linkTo: "/users",
      buttonText: "Gérer les Commerciaux",
      colorClass: "text-blue-500",
    },
    {
      title: "Paramètres de l'Application",
      description:
        "Configurez les paramètres généraux de l'application et les intégrations.",
      icon: Settings,
      linkTo: "/settings",
      buttonText: "Accéder aux Paramètres",
      colorClass: "text-green-500",
    },
  ];

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      // Check if it's a valid date object
      if (!isNaN(d.getTime())) {
        return d.toLocaleString("fr-FR", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      }

      // If invalid, try parsing as number (Unix timestamp)
      // Some APIs return "1712345678" as string
      const num = Number(dateStr);
      if (!isNaN(num) && num > 0) {
        // Guess if it's seconds or milliseconds
        const dateObj = new Date(num > 10000000000 ? num : num * 1000);
        return dateObj.toLocaleString("fr-FR", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      }

      return "Format inconnu";
    } catch (e) {
      return "Erreur date";
    }
  };

  return (
    <div className="px-4 py-4 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-6 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity">
          <BarChartIcon className="h-24 w-24 rotate-12" />
        </div>
        <div className="z-10">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            Tableau de bord
          </h1>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            Centre de pilotage <span className="text-primary font-semibold">AMC Lead Dispatch</span>
          </p>
        </div>
        <div className="z-10 flex gap-2">
          <div className="px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-200 text-[10px] font-semibold text-slate-600 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            LIVE
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
        {featureCards.map((card) => (
          <FeatureCard key={card.title} {...card} />
        ))}
      </div>

      <Card className="border-slate-200 rounded-xl overflow-hidden bg-white">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg border border-slate-100">
              <ListChecks className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-slate-900">Dernières Assignations</CardTitle>
              <CardDescription className="text-xs text-slate-500 font-medium mt-0.5">
                Historique récent des attributions
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (!recentLogs || recentLogs.length === 0) ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-slate-50/50 rounded-2xl animate-pulse"
                >
                  <div className="h-10 w-10 bg-slate-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded-lg w-1/3"></div>
                    <div className="h-3 bg-slate-100 rounded-lg w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentLogs && recentLogs.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {recentLogs.map((log, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-6 hover:bg-slate-50/50 transition-all group"
                >
                  <div className="h-12 w-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
                    <Clock className="h-5 w-5 text-slate-400 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">
                      Lead{" "}
                      <span className="text-primary bg-primary/5 px-2 py-0.5 rounded-lg border border-primary/10">
                        #{log.lead_id}
                      </span>{" "}
                      assigné à{" "}
                      <span className="text-slate-700 font-extrabold underline decoration-primary/20">{log.user_name}</span>
                    </p>
                    <p className="text-xs font-bold text-slate-400 mt-1.5 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-slate-300 inline-block" />
                      {formatDate(log.timestamp)}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-200 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
              ))}
              <div className="p-6 bg-slate-50/30">
                <Button asChild variant="outline" className="w-full font-bold rounded-xl border-slate-200 hover:bg-white hover:shadow-sm transition-all">
                  <Link to="/logs" className="flex items-center justify-center gap-2">
                    Voir tout l'historique ({totalLogsCount})
                    <ListChecks className="h-4 w-4 opacity-50" />
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <ListChecks className="h-10 w-10 text-slate-300" />
              </div>
              <p className="text-slate-500 font-bold text-lg">Aucune assignation</p>
              <p className="text-slate-400 text-sm mt-1">Le système est en attente de nouveaux leads.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
